import { z } from 'zod';
import { emailSchema } from './auth';

/**
 * Login pelo SSO da empresa (T-028d, RF-04; DEC-073): o app pede o início pelo e-mail, o navegador vai ao provedor,
 * a API recebe o retorno, confere tudo e devolve ao app um código de uso único, que o app troca pela sessão provando
 * que é o mesmo aparelho que começou (verificador, como no PKCE).
 */

/** Verificador do aparelho (43 a 128 caracteres base64url, como o code_verifier do PKCE). */
export const ssoVerifierSchema = z.string().regex(/^[A-Za-z0-9_-]{43,128}$/, 'Verificador inválido.');
/** Desafio = SHA-256 do verificador em base64url (43 caracteres). */
export const ssoChallengeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Desafio inválido.');

/** Retorno no desktop: servidor do app só em 127.0.0.1, porta e caminho aleatórios (T-028b). */
export const SSO_LOOPBACK_RETURN = /^http:\/\/127\.0\.0\.1:(\d{1,5})\/sso\/[A-Za-z0-9_-]{16,64}$/;
/** Retorno local válido: o formato acima com uma porta de verdade (1 a 65535). */
export function isLoopbackReturn(url: string): boolean {
  const port = Number(SSO_LOOPBACK_RETURN.exec(url)?.[1] ?? 0);
  return port >= 1 && port <= 65535;
}
/** Retorno no Android: deep link do app (esquema configurado + "://sso"). */
export const SSO_DEEP_LINK_RETURN = /^[a-z][a-z0-9+.-]*:\/\/sso$/;

export const ssoStartInputSchema = z
  .object({
    email: emailSchema,
    /** Plataforma que começou (o retorno volta para ela). iOS fica de fora (DEC-006). */
    platform: z.enum(['WEB', 'DESKTOP', 'ANDROID']).default('WEB'),
    challenge: ssoChallengeSchema,
    /** Para onde o provedor devolve nos apps (a web volta sempre para a própria tela). */
    returnTo: z.string().max(300).optional(),
  })
  .superRefine((v, ctx) => {
    const ok =
      v.platform === 'WEB'
        ? v.returnTo === undefined
        : v.platform === 'DESKTOP'
          ? isLoopbackReturn(v.returnTo ?? '')
          : SSO_DEEP_LINK_RETURN.test(v.returnTo ?? '');
    if (!ok) ctx.addIssue({ code: 'custom', path: ['returnTo'], message: 'Endereço de retorno inválido.' });
  });
export type SsoStartInput = z.input<typeof ssoStartInputSchema>;

/** Endereço do provedor para onde o navegador vai (com state, nonce e PKCE já montados pela API). */
export const ssoStartResponseSchema = z.object({ authorizationUrl: z.string().url() });

/** Código de retorno (vai no fragmento #codigo= do endereço do app). */
export const ssoReturnCodeSchema = z.string().trim().min(20, 'Código inválido.').max(200, 'Código inválido.');
export const ssoExchangeInputSchema = z.object({ code: ssoReturnCodeSchema, verifier: ssoVerifierSchema });
export type SsoExchangeInput = z.input<typeof ssoExchangeInputSchema>;

/** Motivos de falha que voltam ao app no endereço de retorno (#erro=), com a mensagem mostrada na tela. */
export const SSO_RETURN_ERRORS = {
  PROVIDER_ERROR: 'O provedor de login da sua empresa não concluiu a entrada. Tente de novo.',
  LOGIN_EXPIRED: 'A entrada pelo SSO expirou ou já foi usada. Comece de novo.',
  TOKEN_INVALID:
    'A resposta do provedor de login não pôde ser confirmada. Tente de novo ou fale com o administrador.',
  EMAIL_NOT_VERIFIED: 'O provedor não confirmou o seu e-mail. Fale com o administrador da sua empresa.',
  DOMAIN_NOT_ALLOWED: 'A conta usada no provedor não é de um domínio liberado para esta empresa.',
  NO_ACCOUNT: 'Você ainda não tem acesso ao Fluxus BSC nesta empresa. Peça um convite ao administrador.',
  ACCOUNT_DISABLED: 'Seu acesso está desativado. Fale com o administrador da sua empresa.',
  OTHER_ORGANIZATION: 'Este e-mail já tem conta em outra empresa no Fluxus BSC. Fale com a Fluxus.',
  SSO_UNAVAILABLE:
    'O login único da sua empresa não está disponível agora. Entre com e-mail e senha ou fale com o administrador.',
} as const;
export type SsoReturnError = keyof typeof SSO_RETURN_ERRORS;
export const SSO_RETURN_ERROR_CODES = Object.keys(SSO_RETURN_ERRORS) as [SsoReturnError, ...SsoReturnError[]];
export const isSsoReturnError = (v: string): v is SsoReturnError => v in SSO_RETURN_ERRORS;

/** Caminho da tela do app que recebe o retorno do SSO (web). */
export const SSO_RETURN_PATH = '/entrar/sso';
